/**
 * Modelo canónico de viaje (ride) + (de)serialización tolerante.
 *
 * Port de `rider_app/lib/data/models/trip_model.dart`. Mantiene la prioridad
 * `server_phase` > `status` en RTDB y `phase` > `status` en REST, y el parseo
 * de `driver_location.{latitude,longitude}` con {@link parseCoord}.
 *
 * Los campos ausentes se representan como `null` (paridad con los `String?`
 * nullables de Dart), nunca `undefined`.
 */
import { parseCoord } from './parsing';
import { asMap, remoteStatusValue, restPhaseStatusValue, toStr } from './raw';
import { normalizeRideStatus } from './status';

export interface TripRtdb {
  id: string | null;
  invoiceNumber: string | null;
  paymentMethod: string | null;
  carDetails: string | null;
  createdAt: string | null;
  /** Estado ya normalizado al conjunto canónico. */
  status: string | null;
  fares: string | null;
  dropoffAddress: string | null;
  pickupAddress: string | null;
  date: string | null;
  rideType: string | null;
  driverName: string | null;
  driverPhone: string | null;
  driverId: string | null;
  driverPhotoUrl: string | null;
  driverLatitude: number | null;
  driverLongitude: number | null;
}

function emptyTrip(id: string | null = null): TripRtdb {
  return {
    id,
    invoiceNumber: null,
    paymentMethod: null,
    carDetails: null,
    createdAt: null,
    status: null,
    fares: null,
    dropoffAddress: null,
    pickupAddress: null,
    date: null,
    rideType: null,
    driverName: null,
    driverPhone: null,
    driverId: null,
    driverPhotoUrl: null,
    driverLatitude: null,
    driverLongitude: null,
  };
}

/**
 * Deserializa un nodo RTDB `Ride Requests/{rideId}`.
 * @param key clave del snapshot (rideId), o null.
 * @param value valor crudo del snapshot.
 */
export function tripFromSnapshot(key: string | null, value: unknown): TripRtdb {
  if (value === null || value === undefined) {
    return emptyTrip();
  }
  const data = asMap(value) ?? {};
  const driverLocation = asMap(data['driver_location']);

  return {
    id: key,
    invoiceNumber: toStr(data['invoice_number']),
    paymentMethod: toStr(data['payment_method']),
    carDetails: toStr(data['car_details']),
    createdAt: toStr(data['created_at']),
    status: normalizeRideStatus(remoteStatusValue(data)),
    fares: toStr(data['fares']),
    dropoffAddress: toStr(data['dropoff_address']),
    pickupAddress: toStr(data['pickup_address']),
    date: toStr(data['date']),
    rideType: toStr(data['ride_type']),
    driverName: toStr(data['driver_name']),
    driverPhone: toStr(data['driver_phone']),
    driverId: toStr(data['driver_id']),
    driverPhotoUrl: toStr(data['driver_photo_url']),
    driverLatitude: parseCoord(driverLocation?.['latitude']),
    driverLongitude: parseCoord(driverLocation?.['longitude']),
  };
}

/**
 * Deserializa la respuesta REST NestJS (`RideResponseDto`). `pickup`/`dropoff`
 * llegan como `{lat,lng,address}`; el estado se normaliza desde `phase`.
 */
export function tripFromRest(json: Record<string, unknown>): TripRtdb {
  const pickup = asMap(json['pickup']);
  const dropoff = asMap(json['dropoff']);
  const fareSummary = asMap(json['fare_summary']);

  return {
    ...emptyTrip(),
    id: toStr(json['id']),
    paymentMethod: toStr(json['payment_method']),
    createdAt: toStr(json['created_at']),
    status: normalizeRideStatus(restPhaseStatusValue(json)),
    fares: toStr(fareSummary?.['total']),
    dropoffAddress: toStr(dropoff?.['address']),
    pickupAddress: toStr(pickup?.['address']),
    date: toStr(json['scheduled_at']),
    rideType: toStr(json['ride_type']),
    driverId: toStr(json['driver_id']),
  };
}

/** Mapa que se ESCRIBE en RTDB (paridad con `TripModel.toFirebaseMap`). */
export function tripToFirebaseMap(trip: TripRtdb): Record<string, unknown> {
  const map: Record<string, unknown> = {
    invoice_number: trip.invoiceNumber,
    payment_method: trip.paymentMethod,
    car_details: trip.carDetails,
    created_at: trip.createdAt,
    status: trip.status,
    fares: trip.fares,
    dropoff_address: trip.dropoffAddress,
    pickup_address: trip.pickupAddress,
  };
  if (trip.date !== null) map['date'] = trip.date;
  if (trip.rideType !== null) map['ride_type'] = trip.rideType;
  if (trip.driverName !== null) map['driver_name'] = trip.driverName;
  if (trip.driverPhone !== null) map['driver_phone'] = trip.driverPhone;
  if (trip.driverId !== null) map['driver_id'] = trip.driverId;
  if (trip.driverPhotoUrl !== null) map['driver_photo_url'] = trip.driverPhotoUrl;
  return map;
}

/** Estado canónico del viaje (re-normaliza por si acaso). */
export function normalizedTripStatus(trip: TripRtdb): string | null {
  return normalizeRideStatus(trip.status);
}

export function isTripActive(trip: TripRtdb): boolean {
  const s = normalizedTripStatus(trip);
  return s === 'pending' || s === 'accepted' || s === 'arrived' || s === 'in_progress';
}

export function isTripCompleted(trip: TripRtdb): boolean {
  return normalizedTripStatus(trip) === 'completed';
}

export function isTripCancelled(trip: TripRtdb): boolean {
  return normalizedTripStatus(trip) === 'cancelled';
}

/** Tarifa como número, o null. */
export function tripFaresAsNumber(trip: TripRtdb): number | null {
  if (trip.fares === null) return null;
  const n = Number(trip.fares);
  return Number.isNaN(n) ? null : n;
}
